import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Building2, Users, Mail, Activity } from 'lucide-react'

export function AdminDashboardPage() {
  // Fetch stats
  const { data: stats, isLoading } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: async () => {
      const [accountsRes, usersRes, invitationsRes] = await Promise.all([
        supabase.from('accounts').select('id, status', { count: 'exact' }).neq('status', 'deleted'),
        supabase.from('profiles').select('id, status, role', { count: 'exact' }).neq('status', 'deleted'),
        supabase.from('invitations').select('id, accepted_at', { count: 'exact' }),
      ])

      const accounts = accountsRes.data || []
      const users = usersRes.data || []
      const invitations = invitationsRes.data || []

      return {
        totalAccounts: accounts.length,
        activeAccounts: accounts.filter((a) => a.status === 'active').length,
        totalUsers: users.filter((u) => u.role === 'user').length,
        activeUsers: users.filter((u) => u.role === 'user' && u.status === 'active').length,
        totalAdmins: users.filter((u) => u.role === 'admin').length,
        pendingInvitations: invitations.filter((i) => !i.accepted_at).length,
      }
    },
  })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">
          Overview of your platform
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Accounts"
          value={stats?.totalAccounts}
          subtitle={`${stats?.activeAccounts ?? 0} active`}
          icon={<Building2 className="h-4 w-4 text-muted-foreground" />}
          isLoading={isLoading}
          href="/admin/accounts"
        />
        <StatCard
          title="Total Users"
          value={stats?.totalUsers}
          subtitle={`${stats?.activeUsers ?? 0} active`}
          icon={<Users className="h-4 w-4 text-muted-foreground" />}
          isLoading={isLoading}
          href="/admin/users"
        />
        <StatCard
          title="Admins"
          value={stats?.totalAdmins}
          subtitle="Platform administrators"
          icon={<Activity className="h-4 w-4 text-muted-foreground" />}
          isLoading={isLoading}
          href="/admin/users"
        />
        <StatCard
          title="Pending Invitations"
          value={stats?.pendingInvitations}
          subtitle="Awaiting signup"
          icon={<Mail className="h-4 w-4 text-muted-foreground" />}
          isLoading={isLoading}
          href="/admin/users"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Latest platform events</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Activity feed coming in Phase 2
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common administrative tasks</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-muted-foreground">
              - Create new account
            </p>
            <p className="text-sm text-muted-foreground">
              - Invite admin user
            </p>
            <p className="text-sm text-muted-foreground">
              - View all users
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function StatCard({
  title,
  value,
  subtitle,
  icon,
  isLoading,
  href,
}: {
  title: string
  value?: number
  subtitle: string
  icon: React.ReactNode
  isLoading: boolean
  href?: string
}) {
  const navigate = useNavigate()

  return (
    <Card
      className={href ? 'cursor-pointer transition-colors hover:bg-muted/50' : undefined}
      onClick={href ? () => navigate(href) : undefined}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-7 w-16" />
        ) : (
          <div className="text-2xl font-bold">{value ?? 0}</div>
        )}
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </CardContent>
    </Card>
  )
}
